import React from 'react';
import { Truck, Construction, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { services } from '../mock';

const iconMap = {
  truck: Truck,
  construction: Construction,
  'shield-check': ShieldCheck
};

const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Nos <span className="text-orange-400">Services</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Trois métiers complémentaires au service des industriels gabonais :
            transport de matières premières, location d'engins lourds et
            prestations logistiques clé en main.
          </p>
        </div>
      </section>

      {/* Détail des services */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 space-y-12">
          {services.map((service, idx) => {
            const Icon = iconMap[service.icon] || Truck;
            const reverse = idx % 2 === 1;
            return (
              <Card
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="overflow-hidden shadow-xl"
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-5 ${
                    reverse ? 'lg:grid-flow-col-dense' : ''
                  }`}
                >
                  {/* Visuel */}
                  <div
                    className={`lg:col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 relative flex items-center justify-center p-12 min-h-[260px] ${
                      reverse ? 'lg:col-start-4' : ''
                    }`}
                  >
                    <Icon size={120} className="text-orange-400 opacity-80" />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-orange-500 text-white">
                        Service {service.id}
                      </Badge>
                    </div>
                  </div>

                  {/* Contenu */}
                  <CardContent
                    className={`lg:col-span-3 p-8 lg:p-12 ${
                      reverse ? 'lg:col-start-1 lg:row-start-1' : ''
                    }`}
                  >
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-slate-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <h3 className="text-sm font-semibold text-orange-600 uppercase tracking-wider mb-3">
                      Prestations incluses
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                      {service.details.map((detail, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle2
                            size={18}
                            className="text-orange-500 mt-1 flex-shrink-0"
                          />
                          <span className="text-slate-700 text-sm">
                            {detail}
                          </span>
                        </div>
                      ))}
                    </div>

                    {service.typicalGoods.length > 0 && (
                      <>
                        <h3 className="text-sm font-semibold text-orange-600 uppercase tracking-wider mb-3">
                          Marchandises transportées
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {service.typicalGoods.map((g, i) => (
                            <Badge
                              key={i}
                              variant="outline"
                              className="border-orange-300 text-slate-700 bg-orange-50"
                            >
                              {g}
                            </Badge>
                          ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Processus */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Notre <span className="text-orange-500">Processus</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Du premier contact jusqu'au reporting mensuel — chaque étape est
              cadrée.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                step: '01',
                title: 'Audit & étude',
                description:
                  "Analyse du tonnage, des axes, des contraintes HSE et des plages horaires."
              },
              {
                step: '02',
                title: 'Proposition technique',
                description:
                  'Allocation flotte, KPIs, prix au km/tonne ou contrat forfaitaire.'
              },
              {
                step: '03',
                title: 'Mobilisation',
                description:
                  "Conducteurs briefés, GPS configurés, briefing HSE, départ planifié."
              },
              {
                step: '04',
                title: 'Suivi & reporting',
                description:
                  'Dashboard live, reporting mensuel disponibilité & tonnage.'
              }
            ].map((process, index) => (
              <Card
                key={index}
                className="relative overflow-hidden group hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-orange-50 rounded-bl-full"></div>
                <CardContent className="p-6 relative">
                  <div className="text-5xl font-bold text-orange-200 mb-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {process.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {process.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une prestation spécifique ?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Convoi exceptionnel, contrat long terme, mise à disposition
            d'équipages : on dimensionne la solution avec vous.
          </p>
          <a
            href="/contact"
            data-testid="services-cta-contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            Demander un Devis
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;
